import { useEffect, useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, AssetChannelPermission, Channel, Tag, AssetType, BrandEntity, AssetStatus } from '../../types/assetRegistry';
import { AssetCard } from '../../components/assetRegistry/AssetCard';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 13, border: '1.5px solid #e5e7eb',
  borderRadius: 8, outline: 'none', background: '#fff', color: '#111827',
  fontFamily: 'inherit', transition: 'border-color 0.15s',
};
const selectStyle: React.CSSProperties = {
  padding: '8px 12px', fontSize: 13, border: '1.5px solid #e5e7eb',
  borderRadius: 8, outline: 'none', background: '#fff', color: '#111827',
  fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.15s',
};

interface AssetLibraryProps {
  onSelectAsset: (asset: Asset) => void;
  refreshKey: number;
}

export function AssetLibrary({ onSelectAsset, refreshKey }: AssetLibraryProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [permissions, setPermissions] = useState<AssetChannelPermission[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [assetTags, setAssetTags] = useState<{ asset_id: string; tag_id: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterTags, setFilterTags] = useState<string[]>([]);

  useEffect(() => { fetchData(); }, [refreshKey]); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    const [assetsRes, permRes, channelsRes, tagsRes, assetTagsRes] = await Promise.all([
      supabase.from('assets').select('*').order('created_at', { ascending: false }),
      supabase.from('asset_channel_permissions').select('*'),
      supabase.from('channels').select('*').order('name'),
      supabase.from('tags').select('*').order('category').order('name'),
      supabase.from('asset_tags').select('asset_id, tag_id'),
    ]);
    if (assetsRes.data) setAssets(assetsRes.data as Asset[]);
    if (permRes.data) setPermissions(permRes.data as AssetChannelPermission[]);
    if (channelsRes.data) setChannels(channelsRes.data as Channel[]);
    if (tagsRes.data) setTags(tagsRes.data as Tag[]);
    if (assetTagsRes.data) setAssetTags(assetTagsRes.data);
    setLoading(false);
  }

  const filtered = assets.filter((asset) => {
    if (search) {
      const q = search.toLowerCase();
      if (!asset.title.toLowerCase().includes(q) && !(asset.description ?? '').toLowerCase().includes(q)) return false;
    }
    if (filterType && asset.asset_type !== filterType) return false;
    if (filterBrand && asset.brand_entity !== filterBrand) return false;
    if (filterStatus && asset.status !== filterStatus) return false;
    if (filterChannel) {
      const hasPerm = permissions.some(p => p.asset_id === asset.id && p.channel_id === filterChannel && p.permission_type === 'approved');
      if (!hasPerm) return false;
    }
    if (filterTags.length > 0) {
      const ids = assetTags.filter(at => at.asset_id === asset.id).map(at => at.tag_id);
      if (!filterTags.every(tid => ids.includes(tid))) return false;
    }
    return true;
  });

  const tagsByCategory = tags.reduce<Record<string, Tag[]>>((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {});

  const hasFilters = search || filterType || filterBrand || filterStatus || filterChannel || filterTags.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f7f9fb' }}>

      {/* ── Header bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Wizkind Asset Library</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {filtered.length} asset{filtered.length !== 1 ? 's' : ''} {hasFilters ? 'found' : 'total'}
            </p>
          </div>
        </div>

        {/* Filters row */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
            <Search size={14} color="#9ca3af" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 32 }}
              onFocus={e => (e.target.style.borderColor = '#1B4B8A')}
              onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
            />
          </div>

          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}
            onFocus={e => (e.target.style.borderColor = '#1B4B8A')} onBlur={e => (e.target.style.borderColor = '#e5e7eb')}>
            <option value="">All Types</option>
            {(['image','video','copy','email_template','podcast','training_module'] as AssetType[]).map(t => (
              <option key={t} value={t}>{t.replace('_', ' ')}</option>
            ))}
          </select>

          <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} style={selectStyle}
            onFocus={e => (e.target.style.borderColor = '#1B4B8A')} onBlur={e => (e.target.style.borderColor = '#e5e7eb')}>
            <option value="">All Brands</option>
            {(['carebrave','carepreneur','karavista','carebinge','anand_personal','cba_materials'] as BrandEntity[]).map(b => (
              <option key={b} value={b}>{{ carebrave:'CareBravo', carepreneur:'Carepreneur', karavista:'Karavista', carebinge:'CareBinge', anand_personal:'Anand', cba_materials:'CBA' }[b]}</option>
            ))}
          </select>

          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}
            onFocus={e => (e.target.style.borderColor = '#1B4B8A')} onBlur={e => (e.target.style.borderColor = '#e5e7eb')}>
            <option value="">All Statuses</option>
            {(['draft','approved','archived'] as AssetStatus[]).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)} style={selectStyle}
            onFocus={e => (e.target.style.borderColor = '#1B4B8A')} onBlur={e => (e.target.style.borderColor = '#e5e7eb')}>
            <option value="">All Channels</option>
            {channels.map(c => <option key={c.id} value={c.id}>{c.name.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {/* Tag filters */}
        {Object.entries(tagsByCategory).length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280' }}>
              <SlidersHorizontal size={12} /> Tags:
            </span>
            {Object.entries(tagsByCategory).map(([cat, catTags]) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat}:</span>
                {catTags.map(tag => (
                  <button key={tag.id} onClick={() => setFilterTags(prev => prev.includes(tag.id) ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                    style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, border: 'none', cursor: 'pointer',
                      background: filterTags.includes(tag.id) ? '#1B4B8A' : '#f3f4f6',
                      color: filterTags.includes(tag.id) ? '#fff' : '#374151',
                      fontWeight: filterTags.includes(tag.id) ? 600 : 400, transition: 'all 0.15s',
                    }}>
                    {tag.name}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Grid ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
            <div style={{ width: 32, height: 32, border: '3px solid #1B4B8A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, color: '#9ca3af' }}>
            <Search size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>No assets found</p>
            {hasFilters && (
              <button onClick={() => { setSearch(''); setFilterType(''); setFilterBrand(''); setFilterStatus(''); setFilterChannel(''); setFilterTags([]); }}
                style={{ marginTop: 8, fontSize: 13, color: '#1B4B8A', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {filtered.map(asset => (
              <AssetCard key={asset.id} asset={asset} permissions={permissions.filter(p => p.asset_id === asset.id)} onClick={() => onSelectAsset(asset)} />
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
